import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-lg border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-[background-color,border-color,color,box-shadow,translate] focus-visible:transition-none outline-none select-none [-webkit-touch-callout:none] pointer-coarse:min-h-hit-area pointer-coarse:min-w-hit-area focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-sm hover:bg-primary-hover',
        outline:
          'border-border bg-card shadow-xs hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-surface-hover',
        input:
          'border-input bg-card font-normal hover:bg-card aria-expanded:bg-card dark:bg-input/30 dark:hover:bg-input/30 dark:aria-expanded:bg-input/30',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary-hover aria-expanded:bg-secondary aria-expanded:text-secondary-foreground',
        ghost:
          'hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-surface-hover',
        destructive:
          'bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      tone: {
        default: '',
        sidebar:
          'text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-foreground focus-visible:border-sidebar-ring focus-visible:ring-sidebar-ring/50 aria-expanded:bg-sidebar aria-expanded:text-sidebar-muted dark:hover:bg-sidebar-hover',
        'sidebar-quiet': 'text-sidebar-muted hover:text-sidebar-foreground',
        primary: 'text-primary',
        'primary-outline': 'border-primary font-semibold text-primary',
        muted: 'text-muted-foreground',
        'primary-hover': 'text-muted-foreground hover:text-primary disabled:opacity-30',
        'destructive-hover': 'text-muted-foreground hover:text-destructive',
      },
      visibility: {
        default: '',
        reveal: 'opacity-0 transition-opacity pointer-coarse:opacity-100 group-hover:opacity-100 focus-visible:opacity-100',
        'reveal-trigger':
          'opacity-0 transition-opacity pointer-coarse:opacity-100 group-hover:opacity-100 focus-visible:opacity-100 aria-expanded:opacity-100',
      },
      radius: {
        default: '',
        full: 'rounded-full',
      },
      treatment: {
        default: '',
        wrap: '',
        calendar: 'border-transparent bg-transparent px-2 leading-snug shadow-none hover:bg-accent',
        'calendar-selected': 'border-primary/30 bg-selection text-primary hover:bg-accent',
        'calendar-empty': 'border-transparent bg-transparent shadow-none',
      },
      text: {
        default: '',
        xs: 'text-xs',
        numeric: 'tabular-nums',
      },
      spacing: {
        default: '',
        narrow: 'px-2',
        compact: 'gap-1 px-1.5',
      },
      size: {
        default:
          'h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
        xs: "h-6 gap-1 rounded-[min(var(--radius-md),10px)] px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 gap-1 rounded-[min(var(--radius-md),12px)] px-2.5 text-[0.8rem] in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: 'h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
        xl: 'h-control-xl gap-2 px-3.5 has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5',
        field: 'h-8 gap-2 px-2.5',
        sidebar: "h-11 gap-3 px-3 [&_svg:not([class*='size-'])]:size-5",
        'sidebar-icon': "size-11 [&_svg:not([class*='size-'])]:size-5",
        icon: 'size-8',
        'icon-xs':
          "size-6 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
        'icon-sm':
          'size-7 rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg',
        'icon-lg': 'size-9',
        'icon-xl': 'size-control-xl',
        identity: 'h-auto min-h-10 gap-2 px-1',
      },

      appearance: {
        default: '',
        navigation:
          'relative h-auto min-h-11 w-full min-w-0 justify-start gap-3 px-3 py-2 text-left whitespace-normal text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-foreground focus-visible:border-sidebar-ring focus-visible:ring-sidebar-ring aria-[current=page]:border-sidebar-border aria-[current=page]:bg-sidebar-accent aria-[current=page]:text-sidebar-accent-foreground aria-[current=page]:before:absolute aria-[current=page]:before:inset-y-3 aria-[current=page]:before:left-0 aria-[current=page]:before:w-0.5 aria-[current=page]:before:rounded-full aria-[current=page]:before:bg-sidebar-accent-foreground data-collapsed:mx-auto data-collapsed:size-11 data-collapsed:justify-center data-collapsed:px-0 data-collapsed:py-0',
        'chart-action': 'h-5 gap-1 rounded-md px-1.5 py-1 font-mono text-micro shadow-none',
        'column-header': 'h-7 w-full justify-between gap-2 px-0 py-0 hover:bg-transparent',
        'filter-cell':
          'w-full min-w-0 justify-between gap-1.5 font-normal [&>svg]:text-muted-foreground @max-[7rem]/cell:gap-0 @max-[7rem]/cell:[&>svg]:hidden @max-[5rem]/cell:[&_span>svg]:hidden data-invalid:ring-destructive/40 data-invalid:ring-1',
        'filter-action':
          'hover:inset-ring hover:inset-ring-border data-popup-open:inset-ring data-popup-open:inset-ring-border data-[state=open]:inset-ring data-[state=open]:inset-ring-border',
        'filter-combinator': 'px-1.5 font-normal',
        'chip-menu':
          'h-auto self-stretch bg-background dark:bg-input/30 in-data-[size=sm]:min-h-control-sm in-data-[size=default]:min-h-control-md',
        close: 'text-muted-foreground hover:text-foreground rounded-sm p-0.5 transition-colors',
        text: 'font-normal',
        drag: 'text-muted-foreground hover:text-foreground focus:bg-muted focus:text-foreground',
      },
    },
    compoundVariants: [
      {
        treatment: ['wrap', 'calendar', 'calendar-selected', 'calendar-empty'],
        className: 'h-auto min-h-control-md min-w-0 max-w-full py-2 whitespace-normal [overflow-wrap:anywhere]',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
      tone: 'default',
      visibility: 'default',
      radius: 'default',
      treatment: 'default',
      text: 'default',
      spacing: 'default',
    },
  },
);

function Button({
  appearance = 'default',
  className,
  variant = 'default',
  size = 'default',
  tone = 'default',
  visibility = 'default',
  radius = 'default',
  treatment = 'default',
  text = 'default',
  spacing = 'default',
  role,
  onKeyDown,
  onKeyUp,
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
  // Rendered links keep the browser's Enter and Space behavior.
  return (
    <ButtonPrimitive
      data-slot="button"
      {...(role !== undefined ? { role } : {})}
      onKeyDown={
        role === 'link'
          ? (event) => {
              event.preventBaseUIHandler();
              onKeyDown?.(event);
            }
          : onKeyDown
      }
      onKeyUp={
        role === 'link'
          ? (event) => {
              event.preventBaseUIHandler();
              onKeyUp?.(event);
            }
          : onKeyUp
      }
      className={cn(
        buttonVariants({
          variant,
          size,
          tone,
          visibility,
          radius,
          treatment,
          text,
          spacing,
          appearance,
        }),
        className,
      )}
      {...props}
    />
  );
}

export { Button, buttonVariants };
